import React, { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CaretLeft, CaretRight, ArrowRight } from "@phosphor-icons/react";
import { SLIDES, COMPANY } from "@/lib/siteData";

const AUTO = 5000;

export const HeroSlider = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  const next = useCallback(
    () => setI((v) => (v + 1) % SLIDES.length),
    []
  );
  const prev = useCallback(
    () => setI((v) => (v - 1 + SLIDES.length) % SLIDES.length),
    []
  );

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(next, AUTO);
    return () => clearTimeout(timer.current);
  }, [i, paused, next]);

  const slide = SLIDES[i];

  return (
    <section
      id="home"
      data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative h-[450px] md:h-[750px] w-full overflow-hidden bg-[#1a1a1a]"
    >
      {/* Slides */}
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="absolute inset-0 w-full h-full object-cover ken-burns"
            loading="eager"
          />
          <div className="absolute inset-0 slider-overlay" />
        </motion.div>
      </AnimatePresence>

      {/* Vertical red bar */}
      <div className="absolute left-0 top-0 bottom-0 w-[4px] md:w-[6px] bg-[#c41e3a] z-10" />

      {/* Side meta strip */}
      <div className="hidden md:flex absolute top-0 bottom-0 left-[6px] w-12 z-10 items-center justify-center pointer-events-none">
        <span
          style={{ writingMode: "vertical-rl" }}
          className="transform rotate-180 font-mono-upper text-[10px] text-white/60"
        >
          {COMPANY.city}  ·  Est. Saskatoon
        </span>
      </div>

      {/* Content */}
      <div className="relative z-20 h-full max-w-[1400px] mx-auto px-5 md:px-16 flex items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={i + "-text"}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="max-w-[760px] pt-16 md:pt-0"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-[2px] bg-[#c41e3a]" />
              <span className="font-mono-upper text-[11px] text-[#c41e3a]">
                {slide.kicker}
              </span>
            </div>

            <h1
              data-testid={`slide-title-${i}`}
              className="font-display font-black text-white text-[40px] md:text-[80px] leading-[0.95] tracking-[-0.03em]"
            >
              {slide.title.split(" ").map((w, idx, arr) => (
                <span key={idx}>
                  {idx === arr.length - 1 ? (
                    <span className="text-[#c41e3a]">{w}</span>
                  ) : (
                    <>{w} </>
                  )}
                </span>
              ))}
            </h1>

            <p className="font-display font-bold uppercase tracking-[0.1em] text-white/90 text-base md:text-xl mt-4">
              {slide.subtitle}
            </p>
            <p className="font-body text-white/70 text-base md:text-lg mt-5 max-w-xl leading-relaxed">
              {slide.copy}
            </p>

            <div className="flex flex-wrap gap-3 mt-9">
              <a
                href="#contact"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 bg-[#c41e3a] hover:bg-[#e62244] text-white font-bold tracking-[0.22em] uppercase text-[11px] px-6 py-4 transition-colors"
              >
                Request Free Quote
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-3 border border-white/25 hover:border-white text-white font-bold tracking-[0.22em] uppercase text-[11px] px-6 py-4 transition-colors"
              >
                Our Services
              </a>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Controls */}
      <div className="absolute bottom-6 md:bottom-10 right-5 md:right-16 z-30 flex items-center gap-3">
        <button
          type="button"
          onClick={prev}
          data-testid="slider-prev-button"
          aria-label="Previous slide"
          className="w-11 h-11 md:w-12 md:h-12 border border-white/25 hover:bg-[#c41e3a] hover:border-[#c41e3a] text-white flex items-center justify-center transition-colors"
        >
          <CaretLeft size={18} weight="bold" />
        </button>
        <button
          type="button"
          onClick={next}
          data-testid="slider-next-button"
          aria-label="Next slide"
          className="w-11 h-11 md:w-12 md:h-12 border border-white/25 hover:bg-[#c41e3a] hover:border-[#c41e3a] text-white flex items-center justify-center transition-colors"
        >
          <CaretRight size={18} weight="bold" />
        </button>
      </div>

      {/* Dots / counter */}
      <div className="absolute bottom-6 md:bottom-10 left-5 md:left-16 z-30 flex items-center gap-4">
        <div className="flex items-center gap-2" role="tablist">
          {SLIDES.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setI(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              data-testid={`slider-dot-${idx}`}
              className={`h-[3px] transition-all ${
                i === idx
                  ? "w-10 bg-[#c41e3a]"
                  : "w-5 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
        <span className="font-mono-upper text-[11px] text-white/60">
          {String(i + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
        </span>
      </div>
    </section>
  );
};

export default HeroSlider;
